import React from 'react';
import SEO from '../../../components/SEO';
import Layout from '../../../components/Layout';
import Container from '../../../components/Container';
import { Desktop, Tablet } from '../../../constants/responsive';
import { css } from '@emotion/core';
import { FormattedHTMLMessage, useIntl } from 'gatsby-plugin-intl';
import ProfessionalInvestorsBanner from '../../../components/ProfessionalInvestorsBanner';
import ProfessionalInvestorsForm from '../../../components/ProfessionalInvestorsForm';

import AI from '../../../svgs/professional-investors/ai.svg';
import Data from '../../../svgs/professional-investors/data.svg';
import Engine from '../../../svgs/professional-investors/engine.svg';

import Alpha from '../../../svgs/professional-investors/alpha.svg';
import Global from '../../../svgs/professional-investors/global.svg';
import REIT from '../../../svgs/professional-investors/reit.svg';
import China from '../../../svgs/professional-investors/china.svg';
import { mainColor } from '../../../constants/colors';
import ProfessionalInvestorsDisclaimer from '../../../components/ProfessionalInvestorsDisclaimer';
import { createGlobalState } from 'react-use';

const useShowDisclaimer = createGlobalState(true);

const Circle = ({ top = [0, 0, 0], x = [0, 0, 0], right = false }) => {
  return (
    <div
      css={css`
        position: absolute;
        top: ${top[0]}px;
        left: ${right ? 'auto' : '0%'};
        right: ${right ? '0%' : 'auto'};
        background: #ededee;
        opacity: 0.5;
        z-index: -2;
        width: 408px;
        height: 408px;
        border-radius: 408px;
        transform: ${`translateX(${x[0]}px)`};
        ${Tablet} {
          left: 50%;
          right: auto;
          top: ${top[1]}px;
          width: 736px;
          height: 736px;
          border-radius: 736px;
          transform: ${`translateX(${x[1]}px)`};
        }
        ${Desktop} {
          top: ${top[2]}px;
          width: 1148px;
          height: 1148px;
          border-radius: 1148px;
          transform: ${`translateX(${x[2]}px)`};
        }
      `}
    ></div>
  );
};

const MainlandChinaPage = () => {
  const intl = useIntl();

  return (
    <Layout footerBand={false}>
      <SEO
        title={intl.formatMessage({ id: 'professional-investors.title' })}
        description={intl.formatMessage({
          id: 'professional-investors.description',
        })}
        keywords={intl.formatMessage({ id: 'professional-investors.keywords' })}
      />
      <ProfessionalInvestorsDisclaimer useShowDisclaimer={useShowDisclaimer} />
      <ProfessionalInvestorsBanner useShowDisclaimer={useShowDisclaimer}>
        <FormattedHTMLMessage id='professional-investors.title' />
      </ProfessionalInvestorsBanner>
      <Container
        css={css`
          display: flex;
          flex-direction: column;
          margin-top: 80px;
          text-align: left;
          ${Tablet} {
            text-align: center;
          }
          ${Desktop} {
            margin-top: 150px;
          }
        `}
      >
        <h1>
          <FormattedHTMLMessage id='professional-investors.create.title' />
        </h1>
        <h3
          css={css`
            ${Tablet} {
              font-size: 16px;
              line-height: 25px;
              padding: 40px;
            }
            ${Desktop} {
              font-size: 25px;
              line-height: 35px;
              padding: 0px;
            }
          `}
        >
          <FormattedHTMLMessage id='professional-investors.create.text' />
        </h3>
      </Container>
      <Container
        css={css`
          margin-top: 60px;
          ${Desktop} {
            margin-top: 150px;
          }
        `}
      >
        <h1
          css={css`
            ${Tablet} {
              width: 100%;
              text-align: center;
            }
          `}
        >
          <FormattedHTMLMessage id='professional-investors.experience.title' />
        </h1>
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            ${Tablet} {
              flex-direction: row;
              justify-content: space-between;
            }
          `}
        >
          {['ai', 'data', 'engine'].map((item) => {
            const width = 50;
            return (
              <div
                key={item}
                css={css`
                  display: flex;
                  flex-direction: column;
                  ${Tablet} {
                    width: 210px;
                  }
                  ${Desktop} {
                    width: 300px;
                  }
                `}
              >
                <div
                  css={css`
                    width: ${width}px;
                    height: 60px;
                    margin-bottom: 12px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  `}
                >
                  {
                    {
                      ai: <AI width={width} />,
                      data: <Data width={width} />,
                      engine: <Engine width={width} />,
                    }[item]
                  }
                </div>
                <h5
                  css={css`
                    margin-bottom: 12px;
                  `}
                >
                  <b>
                    <FormattedHTMLMessage
                      id={`professional-investors.experience.${item}.title`}
                    />
                  </b>
                </h5>
                <h5>
                  <FormattedHTMLMessage
                    id={`professional-investors.experience.${item}.text`}
                  />
                </h5>
              </div>
            );
          })}
        </div>
      </Container>
      <Container
        css={css`
          margin-top: 100px;
          ${Desktop} {
            margin-top: 150px;
          }
        `}
      >
        <h1
          css={css`
            ${Tablet} {
              width: 100%;
              text-align: center;
              margin-bottom: 50px;
            }
          `}
        >
          <FormattedHTMLMessage id='professional-investors.strategies.title' />
        </h1>
        {['alpha', 'global', 'reit'].map((item) => {
          return (
            <div
              key={item}
              css={css`
                display: flex;
                box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
                border-radius: 12px;
                background: white;

                margin-bottom: ${item !== 'reit' ? 50 : 0}px;
                flex-direction: column;
                padding: 20px;
                ${Tablet} {
                  padding: 40px;
                }
                ${Desktop} {
                  padding: 50px 70px;
                  flex-direction: row;
                }
              `}
            >
              <div
                className='js-fade'
                css={css`
                  width: 40px;
                  height: 40px;
                  border-radius: 85px;
                  background: ${mainColor};
                  margin-right: 16px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  margin-bottom: 12px;
                  ${Tablet} {
                    width: 60px;
                    height: 60px;
                  }
                  ${Desktop} {
                    width: 85px;
                    height: 85px;
                    flex: 0 0 85px;
                    margin-bottom: 0px;
                  }
                `}
              >
                {
                  {
                    alpha: (
                      // <Alpha
                      //   css={css`
                      //     width: 28px;
                      //     ${Tablet} {
                      //       width: 38px;
                      //     }
                      //     ${Desktop} {
                      //       width: 54px;
                      //     }
                      //   `}
                      // />
                      <China
                        css={css`
                          width: 100%;
                        `}
                      />
                    ),
                    global: (
                      <Global
                        css={css`
                          width: 100%;
                        `}
                      />
                    ),
                    reit: (
                      <REIT
                        css={css`
                          width: 20px;
                          ${Tablet} {
                            width: 30px;
                          }
                          ${Desktop} {
                            width: 42px;
                          }
                        `}
                      />
                    ),
                  }[item]
                }
              </div>
              <div
                css={css`
                  ${Desktop} {
                    margin-right: 80px;
                    flex: 0 0 400px;
                  }
                `}
              >
                <h2
                  css={css`
                    color: #0c0f38;
                    font-size: 25px;
                    ${Tablet} {
                      font-size: 30px;
                    }
                  `}
                >
                  <FormattedHTMLMessage
                    id={`professional-investors.strategies.${item}.title`}
                  />
                </h2>
                <div
                  css={css`
                    display: flex;
                    flex-wrap: wrap;
                    margin-bottom: 12px;
                    ${Tablet} {
                      margin-bottom: 24px;
                    }
                    ${Desktop} {
                      margin-bottom: 0px;
                    }
                  `}
                >
                  {[1, 2, 3].map((i) => {
                    return (
                      <div
                        key={i}
                        css={css`
                          height: 38px;
                          border-radius: 40px;
                          background-color: #14123c;
                          color: white;
                          font-size: 12px;
                          font-weight: 600;
                          line-height: 38px;
                          padding: 0px 20px;
                          margin-right: 8px;
                          margin-bottom: 14px;
                        `}
                      >
                        <FormattedHTMLMessage
                          id={`professional-investors.strategies.${item}.tag-${i}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <h4
                css={css`
                  flex: 1 1 100%;
                  color: #0c0f38;
                  font-size: 14px;
                  line-height: 21px;
                  margin-bottom: 0px;
                  ${Tablet} {
                    font-size: 20px;
                    line-height: 30px;
                  }
                `}
              >
                <FormattedHTMLMessage
                  id={`professional-investors.strategies.${item}.text`}
                />
              </h4>
            </div>
          );
        })}
      </Container>
      <Container
        css={css`
          margin-top: 100px;
          ${Desktop} {
            margin-top: 150px;
          }
        `}
      >
        <h2
          css={css`
            width: 100%;
            ${Tablet} {
              text-align: center;
            }
          `}
        >
          <FormattedHTMLMessage id={`professional-investors.fee.title`} />
        </h2>
        <h5
          css={css`
            width: 100%;
            ${Tablet} {
              text-align: center;
            }
          `}
        >
          <FormattedHTMLMessage id={`professional-investors.fee.text`} />
        </h5>
        <div
          css={css`
            display: flex;
            width: 100%;
            justify-content: center;
            flex-direction: column;
            ${Tablet} {
              margin-top: 24px;
              flex-direction: row;
            }
          `}
        >
          {['management', 'performance'].map((item) => {
            return (
              <div
                key={item}
                css={css`
                  width: 100%;
                  height: 95px;
                  color: white;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  flex-direction: column;
                  margin-bottom: 24px;
                  border-radius: 12px;
                  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
                  background: #14123c;
                  ${Tablet} {
                    margin: 0px 8px;
                    width: 320px;
                    height: 140px;
                  }
                  ${Desktop} {
                    width: 400px;
                    height: 190px;
                  }
                `}
              >
                <h2
                  css={css`
                    margin-bottom: 0px;
                    ${Tablet} {
                      margin-bottom: 10px;
                    }
                  `}
                >
                  {{ management: '1 - 1.5%', performance: '10 - 15%' }[item]}
                </h2>
                <h5
                  css={css`
                    margin-bottom: 0px;
                  `}
                >
                  <FormattedHTMLMessage
                    id={`professional-investors.fee.${item}`}
                  />
                </h5>
              </div>
            );
          })}
        </div>
      </Container>
      <ProfessionalInvestorsForm />
      <Circle top={[420, 0, 400]} x={[-220, -800, -1230]} />
      <Circle top={[1100, 700, 850]} x={[220, 0, 50]} right />
      <Circle top={[1700, 1400, 1850]} x={[-220, -700, -1230]} />
      <Circle top={[2500, 2000, 2800]} x={[220, 0, -50]} right />
    </Layout>
  );
};

export default MainlandChinaPage;
